import React, {
	createContext,
	useState,
	useEffect,
	useRef,
	useCallback,
} from 'react';
import { useMediaQuery } from 'react-responsive';
// import { projectSetup } from './project-setup'; // DEPRECATED: Now using appConfig + dynamic setup
import { appConfig } from './app-config';
import { fetchAndProcessCrmData } from './utils/apiUtils';
import { buildApartmentsDataList } from './utils/buildApartmentsList';
import {
	getInitialFiltersConfig,
	filterAndSortApartmentList,
	areFilterConfigsEqual,
} from './utils/filterAndSortApartmentList';
import { VISIBILITY_STATUS } from './constants/visibilityStatus';
import { AVAILABILITY_STATUS } from './constants/availabilityStatus';
import { MODEL_SCOPE } from './constants/modelScope';
import { MOBILE_VIEW_TYPES } from './constants/mobileViewTypes';
import { LAYER_TYPES } from './constants/layerTypes';

const { VISIBLE, HIDDEN, VISIBLE_IN_CRM, HIDDEN_IN_CRM } = VISIBILITY_STATUS;
const { AVAILABLE, SOLD, RESERVED, UNKNOWN } = AVAILABILITY_STATUS;
const { NO_MODEL, SITE, EDIFICE } = MODEL_SCOPE;
const {
	MODEL_VIEW,
	TIERS_VIEW,
	VISUALIZATIONS_VIEW,
	LOCALIZATION_VIEW,
	APARTMENT_VIEW,
	FAVORITES_VIEW,
} = MOBILE_VIEW_TYPES;
const { APARTMENTS_LAYER } = LAYER_TYPES;

/**
 * Fetches and parses a local JSON file.
 * @param {string} filePath - The path to the JSON file.
 * @param {string} [errorMessagePrefix='Error fetching file'] - Prefix for error messages.
 * @returns {Promise<object|null>} The parsed JSON data or null if an error occurs.
 */
async function fetchLocalJSON(
	filePath,
	errorMessagePrefix = 'Error fetching file'
) {
	try {
		const response = await fetch(filePath);
		if (!response.ok) {
			console.error(
				`${errorMessagePrefix} at ${filePath}: ${response.status} ${response.statusText}`
			);
			return null;
		}
		const data = await response.json();
		return data;
	} catch (error) {
		console.error(`Exception during fetch or parse of ${filePath}:`, error);
		return null;
	}
}

/**
 * Safely retrieves a value from a nested object based on the provided path.
 * @param {object} obj - The source object.
 * @param {string[]} path - An array of strings representing the path to the value.
 * @param {*} [defaultValue=undefined] - The value returned if the path does not exist or is invalid.
 * @returns {*} The value found at the path or the defaultValue.
 */
const getKeyValueFromInObjectPath = (obj, path, defaultValue = undefined) => {
	if (!obj || !path || !Array.isArray(path)) {
		return defaultValue;
	}
	let current = obj;
	for (let i = 0; i < path.length; i++) {
		const key = path[i];
		if (
			current &&
			typeof current === 'object' &&
			Object.prototype.hasOwnProperty.call(current, key)
		) {
			current = current[key];
		} else {
			return defaultValue;
		}
	}
	return current;
};

/**
 * Finds a product (apartment) in CRM data based on its ID.
 * @param {string|number} apartmentId - The ID of the apartment to find.
 * @param {object} crmData - Raw CRM data.
 * @param {object} dictionary - The CRM dictionary defining the data structure.
 * @returns {object|null} The found CRM product object or null.
 */
const findCrmProductById = (apartmentId, crmData, dictionary) => {
	if (
		!crmData ||
		!dictionary ||
		!dictionary.object_path_to_units_array ||
		!Array.isArray(dictionary.object_path_to_units_array) ||
		!dictionary.object_path_to_units_array.every(
			(key) => typeof key === 'string'
		) ||
		!dictionary.unit_id_original ||
		typeof dictionary.unit_id_original !== 'string'
	) {
		console.warn(
			'Context: Brak danych CRM lub błąd sciezki do lokali w słowniku.'
		);
		return null;
	}

	const pathToUnits = dictionary.object_path_to_units_array;
	const unitIdOriginalKey = dictionary.unit_id_original;

	const crmUnitsArray = getKeyValueFromInObjectPath(crmData, pathToUnits, []);

	if (!Array.isArray(crmUnitsArray)) {
		console.warn(
			'Context: Ścieżka do jednostek CRM nie rozwiązała się do tablicy.'
		);
		return null;
	}

	return crmUnitsArray.find(
		(p) => p && String(p[unitIdOriginalKey]) === String(apartmentId)
	);
};

/**
 * Calculates the visibility status of an apartment based on CRM data and configuration.
 * @param {object} crmProduct - The CRM product object.
 * @param {object} visibilityConfig - Visibility configuration from the CRM dictionary.
 * @returns {string} The visibility status (e.g., VISIBLE_IN_CRM, HIDDEN_IN_CRM).
 */
const calculateApartmentCrmVisibility = (crmProduct, visibilityConfig) => {
	if (!crmProduct) {
		return HIDDEN_IN_CRM;
	}
	if (
		!visibilityConfig ||
		!visibilityConfig.key ||
		typeof visibilityConfig.key !== 'string' ||
		visibilityConfig.value_visible === undefined
	) {
		// Since crmProduct is truthy, even though visibilityConfig is not defined or incomplete we can assume that the product is visible in CRM
		return VISIBLE_IN_CRM;
	}

	const crmVisibilityValue = crmProduct[visibilityConfig.key];
	if (String(crmVisibilityValue) !== String(visibilityConfig.value_visible)) {
		return HIDDEN_IN_CRM; // W CRM oznaczony jako niewidoczny
	}
	return VISIBLE_IN_CRM; // W CRM oznaczony jako widoczny
};

/**
 * Calculates the availability status of an apartment based on CRM data and configuration.
 * @param {object} crmProduct - The CRM product object.
 * @param {object} availabilityConfig - Availability configuration from the CRM dictionary.
 * @returns {string} The availability status (e.g., AVAILABLE, SOLD, RESERVED, UNKNOWN).
 */
const calculateApartmentCrmAvailability = (crmProduct, availabilityConfig) => {
	if (!crmProduct) {
		return UNKNOWN;
	}
	if (
		!availabilityConfig ||
		!availabilityConfig.key ||
		typeof availabilityConfig.key !== 'string' ||
		!availabilityConfig.available ||
		!availabilityConfig.reserved ||
		!availabilityConfig.sold
	) {
		console.warn(
			'Context: Brak lub niekompletna konfiguracja dostępności w słowniku CRM. Ustawiam UNKNOWN.'
		);
		return UNKNOWN;
	}

	const crmAvailabilityValue = String(crmProduct[availabilityConfig.key]);

	switch (crmAvailabilityValue) {
		case String(availabilityConfig.available):
			return AVAILABLE;
		case String(availabilityConfig.reserved):
			return RESERVED;
		case String(availabilityConfig.sold):
			return SOLD;
		default:
			console.warn(
				`calculateApartmentCrmAvailability: Nieznana wartość statusu dostępności z CRM: "${crmAvailabilityValue}". Ustawiam UNKNOWN.`
			);
			return UNKNOWN;
	}
};

/**
 * Calculates the price of an apartment based on CRM data and configuration.
 * @param {object} crmProduct - The CRM product object.
 * @param {object} priceDataConfig - Price data configuration from the CRM dictionary.
 * @returns {number|null} The apartment price or null.
 */
const calculateApartmentCrmPrice = (crmProduct, priceDataConfig) => {
	if (!crmProduct) {
		return null;
	}
	if (
		!priceDataConfig ||
		!Array.isArray(priceDataConfig.price_keys_checking_order) ||
		priceDataConfig.price_keys_checking_order.length === 0 ||
		!priceDataConfig.price_keys_checking_order.every(
			(key) => typeof key === 'string'
		)
	) {
		console.warn(
			'calculateApartmentCrmPrice: Brak lub niekompletna konfiguracja kluczy cen w słowniku CRM. Ustawiam cenę na null.'
		);
		return null;
	}

	for (const priceKey of priceDataConfig.price_keys_checking_order) {
		if (Object.prototype.hasOwnProperty.call(crmProduct, priceKey)) {
			const rawPrice = crmProduct[priceKey];
			// Sprawdź, czy wartość nie jest null, undefined, pustym stringiem
			if (
				rawPrice !== null &&
				rawPrice !== undefined &&
				String(rawPrice).trim() !== ''
			) {
				const priceNumber = Number(String(rawPrice).replace(',', '.')); // Zamień przecinek na kropkę dla spójności

				// Sprawdź, czy jest to poprawna liczba i większa od 0
				// Czasem ceny '0' oznaczają brak ceny lub cenę na żądanie
				if (!isNaN(priceNumber) && priceNumber > 0) {
					return priceNumber; // Znaleziono poprawną cenę
				}
			}
		}
	}

	// Nie znaleziono żadnej poprawnej ceny po sprawdzeniu wszystkich kluczy
	return null;
};

/**
 * Calculates the area of an apartment based on CRM data and configuration.
 * @param {object} crmProduct - The CRM product object.
 * @param {string} areaConfigKey - The area configuration key from the CRM dictionary.
 * @returns {number|null} The apartment area or null.
 */
const calculateApartmentCrmArea = (crmProduct, areaConfigKey) => {
	if (!crmProduct) {
		return null;
	}
	if (!areaConfigKey || typeof areaConfigKey !== 'string') {
		console.warn(
			'calculateApartmentCrmArea: Brak lub niepoprawna konfiguracja klucza powierzchni w słowniku CRM.'
		);
		return null;
	}

	const areaKey = areaConfigKey;

	if (Object.prototype.hasOwnProperty.call(crmProduct, areaKey)) {
		const rawArea = crmProduct[areaKey];
		if (
			rawArea !== null &&
			rawArea !== undefined &&
			String(rawArea).trim() !== ''
		) {
			const areaNumber = Number(String(rawArea).replace(',', '.'));
			if (!isNaN(areaNumber) && areaNumber > 0) {
				return areaNumber;
			} else {
				console.warn(
					`calculateApartmentCrmArea: Wartość powierzchni z CRM ("${rawArea}") nie jest poprawną liczbą dodatnią dla klucza "${areaKey}".`
				);
				return null;
			}
		}
	}
	return null;
};

export const Context = createContext(null);

const ContextProvider = ({ children, modelId }) => {
	// --- Configuration & Environment ---
	const dataUrl = window.bimWebloData ? window.bimWebloData.urlData : './data/';
	const pageUrl = window.bimWebloData
		? window.bimWebloData.pageUrl
		: window.location.href.split('?')[0];

	// --- Project Setup State (dynamically loaded and merged) ---
	const [projectSetup, setProjectSetup] = useState(null);

	// --- Loading & Error States ---
	const [loading, setLoading] = useState(true);
	const [validationStatus, setValidationStatus] = useState('pending'); // 'pending' | 'valid' | 'error'
	const [crmError, setCrmError] = useState(null);
	const [cmsError, setCmsError] = useState(null);
	const [dictionaryError, setDictionaryError] = useState(null);
	const crmGlobalErrorAlertShown = useRef(false);

	// --- Raw Project & CRM Data ---
	const [projectData, setProjectData] = useState(null); //TODO: rename/refactor this to be siteData
	const [panelSettings, setPanelSettings] = useState(null);
	const [projectStyle, setProjectStyle] = useState(null);
	const [projectEdifices, setProjectEdifices] = useState([]); // Lista nazw/konfiguracji budynków z project.json //TODO: czy ja to w ogóle chcę?
	const [edificesData, setEdificesData] = useState([]);
	const [buildingsData, setBuildingsData] = useState([]); // Lista budynków z modelu 3D (jeśli dostępny)
	const [tiersData, setTiersData] = useState([]);
	const [compartmentsDictionary, setCompartmentsDictionary] = useState([]);
	const [rawCrmData, setRawCrmData] = useState(null);
	const [rawCmsData, setRawCmsData] = useState(null);
	const [crmDictionary, setCrmDictionary] = useState(null);

	// --- Core Application Data (Derived) ---
	const [apartmentsData, setApartmentsData] = useState([]); //refactor whole app to use displayableApartmentsData with filteredApartmentsData as source, rename this to rawApartmentsData
	const [rawApartmentsData, setRawApartmentsData] = useState([]); //refactor whole app to use displayableApartmentsData with filteredApartmentsData as source, rename this to rawApartmentsData
	const [allProcessedApartmentsData, setAllProcessedApartmentsData] = useState(
		[]
	); // processed rawApartmentsData with crm and cms data merged into it
	const [displayableApartmentsData, setDisplayableApartmentsData] = useState(
		[]
	);
	const [arePricesVisible, setArePricesVisible] = useState(true);
	const [apartmentsBlockedWithoutData, setApartmentsBlockedWithoutData] =
		useState(true);

	// --- Active Item States (Apartment, Edifice, Tier) ---
	const [activeApartment, setActiveApartment] = useState(null);
	const [activeApartmentData, setActiveApartmentData] = useState(null);
	const [activeApartmentSelectedTier, setActiveApartmentSelectedTier] = useState(null);
	const [activeBuilding, setActiveBuilding] = useState(null);
	const [activeTier, setActiveTier] = useState(null);

	// --- Filtering & Sorting States ---
	const [filteredApartmentData, setFilteredApartmentData] = useState([]);
	const [isFilterActive, setIsFilterActive] = useState(false);
	// TODO: Dodać stany dla userFilters i currentSortCriteria
	const [initialFiltersConfig, setInitialFiltersConfig] = useState({});
	const [filtersConfig, setFiltersConfig] = useState({});
	const [previousFiltersConfig, setPreviousFiltersConfig] = useState(null);
	const [previousFiltersUIState, setPreviousFiltersUIState] = useState(null); // Stany lokalne UI (selectedExpositions, selectedSupplements, checkboxy)
	const [sortingConfig, setSortingConfig] = useState({
		key: 'id_cad',
		direction: 'asc',
	});

	// --- Favorites States ---
	//MBE: obsługa polubionych
	const [favoriteApartmentUUIDs, setFavoriteApartmentUUIDs] = useState([]);
	const [hasScrolledAfterFirstFavorite, setHasScrolledAfterFirstFavorite] =
		useState(false);
	const [
		filteredApartmentDataWithFavorites,
		setFilteredApartmentDataWithFavorites,
	] = useState([]);
	const rawFilteredDataRef = useRef([]);

	// --- UI & Layout States ---
	const [activeLang, setActiveLang] = useState(
		appConfig?.localization?.default_language || 'pl'
	);
	const [currentModelScope, setCurrentModelScope] = useState(NO_MODEL);
	const [mobileActiveView, setMobileActiveView] = useState(VISUALIZATIONS_VIEW);
	const [showView, setShowView] = useState(NO_MODEL); //TODO: move entirely to modelScope?
	const [activeLayer, setActiveLayer] = useState(APARTMENTS_LAYER);
	const [currentIndex, setCurrentIndex] = useState(15);
	const [layoutList, setLayoutList] = useState(true);
	const [showRotateInstruction, setShowRotateInstruction] = useState(true);
	const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });

	// --- Refs for DOM Elements (Scrolling, etc.) ---
	const visualizationsViewRef = useRef(null);
	const tiersViewRef = useRef(null);
	const overviewLayerRef = useRef(null);
	const apartmentViewRef = useRef(null);
	const favoritesViewRef = useRef(null);
	const currentHashRef = useRef(window.location.hash.replace('#', ''));

	// --- Responsive Design Helpers ---
	const isDesktop = useMediaQuery({ minWidth: 992 });
	const isMobile = useMediaQuery({ maxWidth: 991 });

	// --- Derived Configuration Values (dependent on projectSetup) ---
	// These are computed once projectSetup is loaded
	const crmConfig = React.useMemo(
		() => projectSetup?.website?.crm_integration,
		[projectSetup]
	);
	
	const isExternalCRM = React.useMemo(
		() => crmConfig?.enabled ?? crmConfig?.external_crm,
		[crmConfig]
	);
	
	const isInternalCRM = React.useMemo(
		() => crmConfig?.internal_crm,
		[crmConfig]
	);

	const isSiteModel = React.useMemo(
		() => projectSetup?.site?.render_360 ?? false,
		[projectSetup]
	);
	
	const isBuildingModel = React.useMemo(
		() => projectSetup?.buildings?.render_360 ?? false,
		[projectSetup]
	);
	
	const isModelView = React.useMemo(
		() => isSiteModel || isBuildingModel,
		[isSiteModel, isBuildingModel]
	);

	const initialScope = React.useMemo(() => {
		if (!projectSetup) return NO_MODEL;
		return isSiteModel ? SITE : isBuildingModel ? EDIFICE : NO_MODEL;
	}, [projectSetup, isSiteModel, isBuildingModel]);

	const initialActiveLayer = React.useMemo(
		() => projectSetup?.site?.render_360_initial_layer ?? APARTMENTS_LAYER,
		[projectSetup]
	);

	const initialMobileView = React.useMemo(() => {
		if (!projectSetup) return VISUALIZATIONS_VIEW;
		return initialScope === NO_MODEL ? VISUALIZATIONS_VIEW : MODEL_VIEW;
	}, [projectSetup, initialScope]);

	// --- FUNCTIONS AND HANDLERS -----------------------------------------------------------------
	// --- Local Data Files Fetching Functions ---

	async function fetchAndSetProjectData() {
		const projectDataFromFile = await fetchLocalJSON(
			`${dataUrl}site/site.json`,
			'Error fetching project data'
		);
		if (projectDataFromFile) {
			setProjectData(projectDataFromFile);
			console.log(
			    '%cSite.json data fetched and set to context',
			    'color: green; font-weight: bold;'
			);
		}
		// null case handled in fetchLocalJSON, error logged.
	}

	/**
	 * Gets the current project slug from URL or dataUrl
	 * @returns {string} The backend_slug for the current project
	 */
	function getCurrentProjectSlug() {
		const isStandAloneApp = appConfig?.stand_alone ?? false;
		const appUrl = isStandAloneApp
			? window.location.pathname
			: dataUrl;
		if (appUrl == '/') {
			return 'localhost';  // TODO: IMPORTANT !! REMOVE FOR PRODUCTION
		}
			return appUrl.split('/').filter((part) => part.length > 0).pop();
	}

	/**
	 * Validates that appConfig contains a project matching the current slug
	 * @param {string} slug - The backend_slug to validate
	 * @returns {object} The project configuration object
	 * @throws {Error} If validation fails
	 */
	function validateAppConfig(slug) {
		if (!appConfig) {
			throw new Error('App validation failed: missing appConfig');
		}

		const projectConfig = appConfig.projects.find(
			proj => proj.backend_slug === slug
		);
		
		if (!projectConfig) {
			throw new Error(
				`App validation failed: no project found with slug "${slug}" in appConfig`
			);
		}

		console.log(
			`%cApp config validated for project: ${projectConfig.name || slug}`,
			'color: green; font-weight: bold;'
		);
		
		return projectConfig;
	}

	/**
	 * Future data validation function (placeholder for now)
	 * Will validate that the correct data files are used with this build
	 */
	async function validateDataFiles({
		buildingsCount,
		tiersCount,
		compartmentTypes,
		apartmentsCount,
		compartmentsCount,
	}) {
		// TODO: Implement control number validation
		// const setupControlNumber = setup?.control_number;
		// const appControlNumber = 
		// 	buildingsCount *
		// 	tiersCount * 
		// 	compartmentTypes * 
		// 	apartmentsCount * 
		// 	compartmentsCount;

		// if (setupControlNumber !== appControlNumber) {
		// 	throw new Error(`Data validation failed: wrong data files used`);
		// }

		console.log(
			'%cData files validation: skipped (not yet implemented)',
			'color: orange; font-weight: bold;'
		);
		return true;
	}

	// useEffect(() => {
		// if (
		// 		buildingsData.length === 0 || 
		// 		tiersData.length === 0 || 
		// 		compartmentsDictionary.length === 0 || 
		// 		rawApartmentsData.length === 0
		// ) {
		//  setValidationStatus('pending');
		// 	return;
		// }
		//
		//
		// const totalCompartmentsNumber = rawApartmentsData.reduce((sum, apt) => {
		// 	return sum + (apt.compartments ? apt.compartments.length : 0);
		// }, 0);
		//
		// async function runValidation() {
		// 	try {
		// 		await validateApp({
		// 			appConfig: appConfig,
		// 			buildingsCount: buildingsData.length || 1,
		// 			tiersCount: tiersData.length || 1,
		// 			compartmentTypes: compartmentsDictionary.length || 1,
		// 			apartmentsCount: rawApartmentsData.length || 1,
		// 			compartmentsCount: totalCompartmentsNumber || 1,
		// 		});
		// 		console.log(
		// 			'%cApp validation successful',
		// 			'color: green; font-weight: bold;'
		// 		);
		//		setValidationStatus('validated');
		// 	} catch (error) {
		// 		console.error('App validation error:', error);
		// 		setProjectSetup({});
		//		setValidationStatus('error');
		//		setLoading(true); // Block app if validation fails
		// 	}
		// }

		// runValidation();
	// }, [buildingsData, tiersData, compartmentsDictionary, rawApartmentsData]);

	/**
	 * Fetches panel settings from panel_settings.json if applicable
	 * @param {string} slug - The backend_slug for the current project
	 * @returns {Promise<object|null>} Panel settings object or null
	 */
	async function fetchPanelSettings(slug) {
		const isStandAloneApp = appConfig?.stand_alone ?? false;
		if (isStandAloneApp && !window.bimWebloData) {
			// In stand-alone app mode, panel settings are not used.
			// + fallback for setup mistake if stand_alone = true, 
			// but bimWebloData exists it means stand-alone by mistake, so proceed to fetch panel settings
			console.log(
				'%cPanel settings: skipped (stand-alone mode)',
				'color: orange; font-weight: bold;'
			);
			return null;
		}

		const panelSettingsFromFile = await fetchLocalJSON(
			`${dataUrl}panel_settings.json`,
			'Error fetching panel settings'
		);
		
		if (!panelSettingsFromFile) {
			return null;
		}

		const settings = panelSettingsFromFile.projects?.find(
			(proj) => proj.backend_slug === slug
		);

		if (settings) {
			console.log(
				'%cPanel settings fetched successfully',
				'color: green; font-weight: bold;'
			);
		} else {
			console.warn(
				`Panel settings file found but no settings for project "${slug}"`
			);
		}

		return settings || null;
	}

	/**
	 * Initializes projectSetup by merging appConfig project with panel settings
	 * @param {string} slug - The backend_slug for the current project
	 * @returns {Promise<object>} The final merged projectSetup
	 */
	async function initializeProjectSetup(slug) {
		// 1. Validate and get project from appConfig
		const projectConfig = validateAppConfig(slug);

		// 2. Fetch panel settings if applicable
		const panelSettingsForProject = await fetchPanelSettings(slug);

		// 3. Store panel settings in state (for potential separate use)
		setPanelSettings(panelSettingsForProject);

		// 4. Merge panel settings with project config
		// Panel settings override appConfig values
		const mergedSetup = panelSettingsForProject
			? deepMerge(projectConfig, panelSettingsForProject)
			: projectConfig;

		console.log(
			'%cProject setup initialized and ready',
			'color: green; font-weight: bold;',
			mergedSetup
		);

		return mergedSetup;
	}

	/**
	 * Deep merge utility for merging panel settings with project config
	 * @param {object} target - Base object (projectConfig)
	 * @param {object} source - Override object (panelSettings)
	 * @returns {object} Merged object
	 */
	function deepMerge(target, source) {
		const output = { ...target };
		
		if (isObject(target) && isObject(source)) {
			Object.keys(source).forEach(key => {
				if (isObject(source[key])) {
					if (!(key in target)) {
						output[key] = source[key];
					} else {
						output[key] = deepMerge(target[key], source[key]);
					}
				} else {
					output[key] = source[key];
				}
			});
		}
		
		return output;
	}

	function isObject(item) {
		return item && typeof item === 'object' && !Array.isArray(item);
	}

	/**
	 * Fetches style.json, processes it, and sets the projectStyle state.
	 */
	async function fetchAndSetProjectStyle() {
		let styleDataFromFile = await fetchLocalJSON(
			`${dataUrl}website/style.json`,
			'Error fetching project style'
		);
		if (styleDataFromFile) {
			const roomColors = Object.keys(styleDataFromFile)
				.filter((key) => key.startsWith('__room'))
				.map((key) => styleDataFromFile[key]);
			styleDataFromFile.roomsColors = roomColors;

			setProjectStyle(styleDataFromFile);
			// console.log(
			// 	'%cProject style fetched and set to context',
			//     'color: green; font-weight: bold;'
			// );
			// console.log(styleDataFromFile);
		}
	}

	/**
	 * Fetches compartments.json and sets the compartmentsDictionary state.
	 */
	async function fetchAndSetCompartmentsDictionary() {
		const compartmentsDataFromFile = await fetchLocalJSON(
			`${dataUrl}website/compartments.json`,
			'Error fetching compartments dictionary'
		);
		if (compartmentsDataFromFile && compartmentsDataFromFile?.compartments_types) {

			const compartmentTypes = [];
			for (const [key, value] of Object.entries(
				compartmentsDataFromFile.compartments_types
			)) {
				compartmentTypes.push({ id: key, ...value });
			}
			// console.log(compartmentsDataFromFile.compartments_types);
			setCompartmentsDictionary(compartmentTypes);
			// console.log(
			//     '%cCompartments dictionary fetched and set to context',
			//     'color: green; font-weight: bold;'
			// );
		}
	}

	async function fetchAndSetBuildingsData() {
		const buildingsDataFromFile = await fetchLocalJSON(
			`${dataUrl}/buildings/buildings.json`,
			'Error fetching buildings data'
		);
		if (buildingsDataFromFile && Array.isArray(buildingsDataFromFile.buildings)) {
			
			if (!buildingsDataFromFile.buildings.length) {
				setBuildingsData([]);
				console.warn('Fetched buildings data, but the buildings array is empty.');
			} else {
				const sortedBuildings = buildingsDataFromFile.buildings.sort((a, b) =>
					a.id_cad.localeCompare(b.id_cad)
				);
				setBuildingsData(sortedBuildings);
				if (sortedBuildings.length === 1) {
					const loneBuildingName = sortedBuildings[0].name_original;
					setActiveBuilding(loneBuildingName); //TODO: do we move to id_cad? Test this? does setting activeBuilding here cause any issues?
				}
				console.log(
					'%cBuildings data fetched and set to context',
					'color: green; font-weight: bold;'
				);
			}
		} else {
			setBuildingsData([]);
			console.warn('Fetched buildings data, but no valid array found in `data.buildings`.');
		}
	}

	async function fetchAndSetRawApartmentsData() {
		const apartmentsDataFromFile = await fetchLocalJSON(
			`${dataUrl}/apartments/apartments.json`,
			'Error fetching apartments data'
		);
		if (apartmentsDataFromFile && Array.isArray(apartmentsDataFromFile.apartments)) {

			if (!apartmentsDataFromFile.apartments.length) {
				setRawApartmentsData([]);
				console.warn('Fetched apartments data, but the apartments array is empty.');
			} else {
				const sortedApartments = apartmentsDataFromFile.apartments.sort((a, b) =>
					a.id_cad.localeCompare(b.id_cad)
				);
				setRawApartmentsData(sortedApartments);
				console.log(
					'%cApartments data fetched and set to context',
					'color: green; font-weight: bold;'
				);
			}
		} else {
			setRawApartmentsData([]);
			console.warn('Fetched apartments data, but no valid array found in `data.apartments`.');
		}
	}

	// --- URL params and Hash Management ---
	const updateUrlHash = (id) => {
		if (currentHashRef.current !== id) {
			currentHashRef.current = id;
			window.history.pushState({ section: id }, '', `#${id}`);
		}
	};

	const scrollToSection = (sectionId) => {
		if (sectionId) {
			const element = document.getElementById(sectionId);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: 'start' });
			} else {
				console.warn(
					`[Context] scrollToSection: Element with ID "${sectionId}" not found.`
				);
			}
		}
	};

	// --- Functions and handlers for Favorite Apartments ---
	/**
	 * @callback addFavoriteFlag
	 * @param {Array<object>} apartmentsList - The list of apartments.
	 * @param {Array<string>} favUUIDs - An array of UUIDs of favorite apartments.
	 * @returns {Array<object>} A new list of apartments with an `isFavorite` boolean flag.
	 * @description Pure utility function to add an `isFavorite` flag to each apartment in a list.
	 */
	const addFavoriteFlag = useCallback((apartmentsList, favUUIDs) => {
		if (!apartmentsList) return [];
		return apartmentsList.map((apt) => ({
			...apt,
			isFavorite: favUUIDs.includes(apt.uuid),
		}));
	}, []);

	const updateFilteredApartmentsWithFavoriteFlag = useCallback(() => {
		const augmented = addFavoriteFlag(
			rawFilteredDataRef.current,
			favoriteApartmentUUIDs
		);
		setFilteredApartmentDataWithFavorites(augmented);
	}, [favoriteApartmentUUIDs, addFavoriteFlag]);

	const handleSetFilteredApartmentData = (newRawFilteredData) => {
		rawFilteredDataRef.current = newRawFilteredData;
		// updateFilteredApartmentsWithFavoriteFlag();
	};

	/**
	 * @callback toggleFavoriteApartmentHandler
	 * @param {string} uuid - The UUID of the apartment to toggle in favorites.
	 * @description Toggles the favorite status of an apartment, updating both the
	 * `favoriteApartmentUUIDs` state and localStorage.
	 */
	const toggleFavoriteApartmentHandler = useCallback((uuid) => {
		setFavoriteApartmentUUIDs((prevFavorites) => {
			let updatedFavorites;
			if (prevFavorites.includes(uuid)) {
				updatedFavorites = prevFavorites.filter((fav) => fav !== uuid);
			} else {
				updatedFavorites = [...prevFavorites, uuid];
			}
			localStorage.setItem('favorites', JSON.stringify(updatedFavorites));
			return updatedFavorites;
		});
	}, []);

	/**
	 * @callback removeAllFavoritesHandler
	 * @description Removes all apartments from favorites, updating both the
	 * `favoriteApartmentUUIDs` state and localStorage.
	 */
	const removeAllFavoritesHandler = useCallback(() => {
		setFavoriteApartmentUUIDs([]);
		localStorage.setItem('favorites', JSON.stringify([]));
	}, []);

	// --- Apartments List filtering and sorting handlers ---
	const resetAllFiltersHandler = (localUIState = null) => {
		console.log('resetAllFiltersHandler');
		if (isFilterActive) {
			setPreviousFiltersConfig(filtersConfig);
			if (localUIState) {
				setPreviousFiltersUIState(localUIState);
			}
		}
		setFiltersConfig(initialFiltersConfig);
	};

	const restorePreviousFiltersHandler = () => {
		console.log('restorePreviousFiltersHandler');
		if (previousFiltersConfig) {
			console.log('restorePreviousFiltersHandler, restoring:', previousFiltersConfig);
			setFiltersConfig(previousFiltersConfig);
			// setPreviousFiltersConfig(null);
		}
	};

	const applyFiltersHandler = (newFilters) => {
		const newConfig = { ...filtersConfig, ...newFilters };
		const areNewFiltersSameAsInitial = areFilterConfigsEqual(
			newConfig,
			initialFiltersConfig
		);
		setFiltersConfig((prevConfig) => ({
			...prevConfig,
			...newFilters,
		}));
		// setIsFilterActive(!areNewFiltersSameAsInitial);
		setPreviousFiltersConfig(areNewFiltersSameAsInitial ? previousFiltersConfig : null);
		setActiveLayer(APARTMENTS_LAYER);
	};


	const applySortingHandler = (newSortConfig) => {
		setSortingConfig(newSortConfig);
	};

	// --- Main interaction and navigation handlers --- //(eksportowane do komponentów)

	/**
	 * @callback setActiveApartmentHandle
	 * @param {string | number | null} id - The original ID (`id_original`) of the apartment to set as active, or `null` to reset the active apartment.
	 * @description Handles the logic for setting the active apartment.
	 * It validates the provided ID, checks apartment availability (e.g., if sold and not accessible based on `projectSetup`),
	 * updates relevant states like `activeApartment`, `activeTier`, `activeBuilding`, and `currentModelScope`.
	 * It also updates the URL 'apt' parameter and handles view changes for mobile devices.
	 */
	const setActiveApartmentHandle = useCallback(
		(id, tier = null) => {
			const resetActiveApartment = () => {
				const url = new URL(window.location);
				url.searchParams.delete('apt');
				window.history.replaceState({}, '', url.toString());
				setActiveApartment(null);
				setActiveApartmentSelectedTier(null);
			};
			console.log(`[DEBUG] setActiveApartmentHandle called with id: ${id} and tier: ${tier}`);
			// Explicitly check for null to reset
			if (id === null) {
				resetActiveApartment();
				return;
			}
			// Check for other falsy values or non-string/non-number types that are not null
			if (!id || (typeof id !== 'string' && typeof id !== 'number')) {
				console.warn(
					`[Context] setActiveApartmentHandle called with invalid ID: ${id}. Current active apartment (${activeApartment}) will be maintained. Please check the calling code.`
				);
				alert(
					`Upss, coś poszło nie tak. Podano nieprawidłowy indentyfikator lokalu, więc nie zmieniono widoku`
				); //TODO: localize this message
				return;
			}

			if (!displayableApartmentsData || displayableApartmentsData.length === 0) {
				console.warn(
					'[Context] setActiveApartmentHandle called but apartmentsData is not ready. Aborting. Current active apartment maintained.'
				);
				return;
			}

			const targetIdString = String(id);
			const targetApartment = displayableApartmentsData.find(
				(apt) => String(apt.id_original) === targetIdString
			);
			if (!targetApartment) {
				console.warn(
					`[Context] setActiveApartmentHandle: Apartment with id "${targetIdString}" not found in displayableApartmentsData. Current active apartment (${activeApartment}) will be maintained.`
				);
				alert(
					`Upss, coś poszło nie tak. Nie znaleziono lokalu o ID: "${targetIdString}", więc nie zmieniono widoku`
				); //TODO: localize this message
				return;
			}

			const isSoldAndNotAccessible =
				projectSetup &&
				projectSetup.website &&
				targetApartment.availability === SOLD && //TODO: refactor bim-weblo-panel to use 'sold' instead of 'sell' or integer 3 (1 = available, 2 = reserved, 3 = sold)
				!projectSetup.website.are_sold_apartments_accessible;

			if (isSoldAndNotAccessible) {
				alert(`Przykro nam, lokal "${targetIdString}" nie jest już dostępny.`);
				return;
			}
			//TODO: What if current active apartment becomes sold and sold apartments are not accessible?
			// if ( activeApartment &&	activeApartment !== targetIdString) {
					setActiveApartment(targetIdString);
					console.log('[DEBUG] setActiveApartment to arg. tier ', targetIdString);
			// }
			
			// Set activeApartmentSelectedTier and activeTier based on passed tier parameter
			if (tier !== null && tier !== undefined) {
				setActiveApartmentSelectedTier(tier);
				console.log('[DEBUG] setActiveApartmentSelectedTier to arg tier ', tier);
				// console.log('setActiveApartmentSelectedTier to ', tier);
				if (activeTier !== tier) {
					setActiveTier(tier);
					console.log('[DEBUG] setActiveTier to arg. tier ', tier);
				}
			} else {
				// No tier specified - always reset to apartment's base tier
				// This ensures switching between apartments resets tier selection
				console.log('[DEBUG] No tier specified');
				if (targetApartment.ref_tier !== undefined) {
					console.log('[DEBUG] setActiveApartmentSelectedTier to targetApartment.ref_tier (reset)', targetApartment.ref_tier);
					setActiveApartmentSelectedTier(targetApartment.ref_tier); // Reset tier selection
					if (activeTier !== targetApartment.ref_tier) {
						setActiveTier(targetApartment.ref_tier);
						console.log('[DEBUG] setActiveTier to base tier:', targetApartment.ref_tier);
					}
				}
			}			
			const targetApartmentBuildingId = targetApartment.building_id;
			const targetApartmentBuildingName = buildingsData.find(
				(bld) => String(bld.id_cad) === String(targetApartmentBuildingId)
			 )?.name_original;
			// setActiveBuilding(targetApartmentBuildingName); //TODO move to building_id	
			if (
				targetApartmentBuildingId &&
				activeBuilding !== targetApartmentBuildingName
			) {
				setActiveBuilding(targetApartmentBuildingName); //TODO move to building_id
				// TODO: decide if we need to change the model scope:
				// if (currentModelScope !== EDIFICE) {
				//     setCurrentModelScope(EDIFICE);
				// but add check if edifice is available in the model!!
				// }
			}

			const url = new URL(window.location);
			// Only update URL and history if the 'apt' param actually changes
			if (url.searchParams.get('apt') !== targetIdString) {
				url.searchParams.set('apt', encodeURIComponent(targetIdString));
				window.history.replaceState(
					{ apartmentId: targetIdString },
					'',
					url.toString()
				);
			} else {
				// if popstate sets an apt from URL that was already there, but history state was different ensure history state is consistent
				const currentState = window.history.state;
				if (!currentState || currentState.apartmentId !== targetIdString) {
					window.history.replaceState(
						{ apartmentId: targetIdString },
						'',
						url.toString()
					);
				}
			}

			if (isDesktop) {
				// setShowView(1); //what does this even do and what for... TODO: switch between ModelView and VisualizationView!
				// Scroll to apartment view after setting active apartment
				setTimeout(() => {
					if (apartmentViewRef.current) {
						apartmentViewRef.current.scrollIntoView({
							behavior: "smooth",
							block: "start",
						});
					}
				}, 500);
			} else if (mobileActiveView !== APARTMENT_VIEW) {
				setMobileActiveView(APARTMENT_VIEW);
			}
	},
	[
		displayableApartmentsData,
		activeBuilding,
		mobileActiveView,
		isDesktop,
		setMobileActiveView,
		setShowView,
		apartmentViewRef,
		// projectSetup.website.are_sold_apartments_accessible,
	]
);	/**
	 * Function to navigate to the next or previous apartment in the filtered list.
	 * @param {number} direction - 1 for next apartment, -1 for previous apartment.
	 *  @returns {string|null} - Returns null on error or the ID of the currently active apartment if no change is made, If change is valid, returns the ID of the new active apartment ID and sets it as activeApartment in Context.
	 */
	const navigateToApartmentInDirectionHandler = useCallback(
		(direction) => {
			if (
				!activeApartment ||
				!filteredApartmentDataWithFavorites ||
				filteredApartmentDataWithFavorites.length === 0 ||
				!projectSetup
			) {
				console.warn(
					'[Context] Cannot navigate: missing activeApartment, filtered list, or projectSetup.'
				);
				return null; // Zwróć null lub ID, jeśli coś poszło nie tak lub nie ma zmiany
			}

			// Zakładamy, że filteredApartmentDataWithFavorites jest już posortowana tak, jak w ApartmentHeading
			const sortedList = filteredApartmentDataWithFavorites; // Użyj bezpośrednio, jeśli jest już posortowana
			// Jeśli nie, musiałbyś ją posortować tutaj:
			// const sortedList = [...filteredApartmentDataWithFavorites].sort((a, b) => { /* Twoja logika sortowania */ });

			const numberOfApartments = sortedList.length;
			const activeApartmentIndex = sortedList.findIndex(
				(apt) => apt.id_original === activeApartment
			);

			if (numberOfApartments === 0) {
				// console.log("[Context] No apartments in the filtered list to navigate.");
				// Visibly inform user? alert rather than console?
				return null;
			}

			if (numberOfApartments === 1) {
				// console.log("[Context] Only one apartment in the list, no navigation needed.");
				// Możesz zdecydować, czy chcesz wywołać setActiveApartmentHandle z tym samym ID,
				// aby np. zresetować jakieś stany podrzędne, jeśli to konieczne.
				// Na razie: nie rób nic, jeśli jest tylko jeden i jest już aktywny.
				if (activeApartment !== sortedList[0].id_original) {
					setActiveApartmentHandle(sortedList[0].id_original, sortedList[0].ref_tier);
					console.log(`setActiveApartmentHandle(sortedList[0].id_original, sortedList[0].ref_tier);`, sortedList[0].id_original, sortedList[0].ref_tier )
					return sortedList[0].id_original;
				} //else visibly alert user why there is no change
				return activeApartment;
			}
			
			if (activeApartmentIndex === -1) {
				// Aktywny apartament nie jest w przefiltrowanej liście (np. został odfiltrowany)
				// Wybierz pierwszy (dla direction 1) lub ostatni (dla direction -1) z przefiltrowanej listy
				const targetIndex = direction === 1 ? 0 : numberOfApartments - 1;
				if (sortedList[targetIndex]) {
					// console.log(`[Context] Active apt not in filtered list. Navigating to ${direction === 1 ? 'first' : 'last'}.`);
					console.log(`setActiveApartmentHandle(sortedList[targetIndex].id_original, sortedList[targetIndex].ref_tier);`, sortedList[targetIndex].id_original, sortedList[targetIndex].ref_tier )
					setActiveApartmentHandle(sortedList[targetIndex].id_original, sortedList[targetIndex].ref_tier);
					return sortedList[targetIndex].id_original;
				}
				return null; // Nie znaleziono celu //What does it mean? that sortedList is empty? if so visibly alert user why there is no change
			}

			let currentTestIndex = activeApartmentIndex;

			for (let i = 0; i < numberOfApartments; i++) {
				currentTestIndex =
					(currentTestIndex + direction + numberOfApartments) %
					numberOfApartments;
				const potentialApartment = sortedList[currentTestIndex];

				if (!potentialApartment) continue; // Dodatkowe zabezpieczenie

				const isSold =
					potentialApartment.availability === AVAILABILITY_STATUS.SOLD; // Użyj stałej

				if (projectSetup.website.are_sold_apartments_accessible || !isSold) {
					if (currentTestIndex !== activeApartmentIndex) {
						// Upewnij się, że faktycznie zmieniamy apartament
						console.log(`[Context] Navigating to new apartment: ${potentialApartment.id_original} with tier ${potentialApartment.ref_tier}`);
						setActiveApartmentHandle(potentialApartment.id_original, potentialApartment.ref_tier);
						return potentialApartment.id_original; // Zwróć ID nowego apartamentu
					} else {
						// Jeśli pętla wróciła do tego samego (np. wszystkie inne są sprzedane i niedostępne)
						// console.log("[Context] Navigation looped back to the same apartment or no valid next/prev found.");
						return activeApartment; // Zwróć obecne ID
					}
				}
			}
			// Jeśli pętla przeszła przez wszystkie i nie znalazła odpowiedniego (bardzo mało prawdopodobne, jeśli jest co najmniej jeden dostępny)
			console.warn(
				'[Context] No suitable apartment found for navigation in the given direction.'
			);
			return activeApartment; // Zwróć obecne ID
		},
		[
			activeApartment,
			filteredApartmentDataWithFavorites,
			projectSetup,
			setActiveApartmentHandle,
		]
	);

	// --- USE EFFECTS ----------------------------------------------------------------------------
	
	/**
	 * @description Main initialization effect - runs once on mount
	 * 1. Gets current project slug
	 * 2. Validates appConfig and initializes projectSetup
	 * 3. Fetches all required data files
	 * 4. Sets validation status and loading state
	 */
	useEffect(() => {
		let isMounted = true;

		async function initializeApp() {
			try {
				console.log('%c=== App Initialization Started ===', 'color: blue; font-weight: bold;');
				
				// Step 1: Get project slug
				const projectSlug = getCurrentProjectSlug();
				console.log(`Current project slug: ${projectSlug}`);

				// Step 2: Initialize projectSetup (validates config + merges panel settings)
				const setup = await initializeProjectSetup(projectSlug);
				
				if (!isMounted) return;
				
				setProjectSetup(setup);
				setValidationStatus('validated');

				// Step 3: Fetch all data files in parallel
				await Promise.all([
					fetchAndSetProjectData(),
					fetchAndSetProjectStyle(),
					fetchAndSetCompartmentsDictionary(),
					fetchAndSetBuildingsData(),
					fetchAndSetRawApartmentsData(),
				]);

				if (!isMounted) return;

				console.log('%c=== App Initialization Completed ===', 'color: blue; font-weight: bold;');
				
				// Loading will be set to false by another useEffect once all data is processed
			} catch (error) {
				console.error('%c=== App Initialization Failed ===', 'color: red; font-weight: bold;', error);
				
				if (!isMounted) return;
				
				setValidationStatus('error');
				setProjectSetup(null);
				// Keep loading true to block app
				alert(`Błąd inicjalizacji aplikacji: ${error.message}`);
			}
		}

		initializeApp();

		return () => {
			isMounted = false;
		};
	}, []); // Run once on mount

	/**
	 * @description Updates UI states when projectSetup becomes available
	 */
	useEffect(() => {
		if (!projectSetup) return;

		// Update UI states based on projectSetup
		if (initialScope !== NO_MODEL) {
			setCurrentModelScope(initialScope);
			setShowView(initialScope);
		}
		
		if (initialActiveLayer !== APARTMENTS_LAYER) {
			setActiveLayer(initialActiveLayer);
		}
		
		if (initialMobileView !== VISUALIZATIONS_VIEW) {
			setMobileActiveView(initialMobileView);
		}

		console.log('%cUI states initialized based on projectSetup', 'color: green; font-weight: bold;');
	}, [projectSetup, initialScope, initialActiveLayer, initialMobileView]);

	// ---
	/**
	 * @description Updates `activeApartmentData` (the full apartment object)
	 * whenever `activeApartment` (the ID) or the main `apartmentsData` list changes.
	 * Clears `activeApartmentData` if the ID is not found or if dependencies are missing.
	 */
	useEffect(() => {
		// if (activeApartment && apartmentsData && apartmentsData.length > 0) {
		if (activeApartment && displayableApartmentsData && displayableApartmentsData.length > 0) {
			//   console.log("[Context] Updating activeApartmentData. activeApartment ID:", activeApartment);
			const foundApartment = displayableApartmentsData.find(
				(item) => String(item.id_original) === String(activeApartment)
			);
			if (foundApartment) {
				setActiveApartmentData(foundApartment);
				// console.log("[Context] activeApartmentData SET to:", foundApartment.id_original);
			} else {
				console.warn(
					'[Context] Apartment not found for ID:',
					activeApartment,
					'Clearing activeApartmentData.'
				);
				setActiveApartmentData(null); // Lub obsłuż inaczej, jeśli apartament nie zostanie znaleziony
			}
		} else {
			// Jeśli nie ma activeApartment lub apartmentsData, wyczyść activeApartmentData
			if (activeApartmentData !== null) {
				// Tylko jeśli faktycznie się zmienia
				console.log(
					'[Context] Clearing activeApartmentData because activeApartment or apartmentsData is missing.'
				);
				setActiveApartmentData(null);
			}
		}
	}, [
		activeApartment,
		// apartmentsData,
		displayableApartmentsData,
	]);

	/**
	 * @description Loads favorite apartment UUIDs from `localStorage` on initial component mount.
	 * Handles potential parsing errors and ensures the loaded data is an array.
	 */
	useEffect(() => {
		const savedFavorites = localStorage.getItem('favorites');
		if (savedFavorites) {
			try {
				const parsedFavorites = JSON.parse(savedFavorites);
				if (Array.isArray(parsedFavorites)) {
					setFavoriteApartmentUUIDs(parsedFavorites);
				} else {
					console.warn('Favorites in localStorage is not an array, resetting.');
					localStorage.setItem('favorites', JSON.stringify([]));
					setFavoriteApartmentUUIDs([]);
				}
			} catch (error) {
				console.error('Error parsing favorites from localStorage:', error);
				localStorage.setItem('favorites', JSON.stringify([]));
				setFavoriteApartmentUUIDs([]);
			}
		}
	}, []);

	/**
	 * @description Scrolls to the favorites view on desktop when the first favorite is added,
	 * but only if it hasn't scrolled before for this session.
	 */
	useEffect(() => {
		if (isMobile) return;
		if (
			favoriteApartmentUUIDs.length === 1 &&
			favoritesViewRef.current &&
			!hasScrolledAfterFirstFavorite
		) {
			favoritesViewRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
			setHasScrolledAfterFirstFavorite(true);
		}
	}, [favoriteApartmentUUIDs, hasScrolledAfterFirstFavorite]);

	/**
	 * @description Handles the browser's 'popstate' event (back/forward buttons)
	 * to synchronize active apartment and section scrolling.
	 */
	useEffect(() => {
		const handlePopState = (event) => {
			const state = event.state;
			const currentUrlParams = new URLSearchParams(window.location.search);
			const aptFromCurrentUrl = currentUrlParams.get('apt');
			const currentHash = window.location.hash.substring(1);
			const section = event.state?.section;
			// console.log('[Context] popstate event triggered. State:', state, 'URL apt:', aptFromCurrentUrl, 'Hash:', currentHash);

			if (state && state.apartmentId !== undefined) {
				// History entry was primarily for an apartment
				// console.log('[Context] popstate: Restoring apartment from state:', state.apartmentId);
				setActiveApartmentHandle(state.apartmentId); // This will also handle URL consistency for ?apt=

				// If this state also had a section, try to scroll to it
				if (state.sectionId) {
					// Consider a slight delay if setActiveApartmentHandle might cause layout shifts
					// setTimeout(() => scrollToSection(state.sectionId), 0);
				}
			} else if (state && state.sectionId) {
				// History entry was primarily for a section scroll
				// console.log('[Context] popstate: Restoring section from state:', state.sectionId);
				// scrollToSection(state.sectionId);
				// Ensure active apartment is still in sync with URL's apt param
				if (String(activeApartment) !== String(aptFromCurrentUrl)) {
					// console.log('[Context] popstate: Apt param changed or desynced during section nav, re-syncing from URL apt:', aptFromCurrentUrl);
					setActiveApartmentHandle(aptFromCurrentUrl || null);
				}
			} else {
				// No specific state from history.go/back, or initial popstate on page load for some browsers.
				// Rely on the URL itself.
				// console.log('[Context] popstate: No specific state, syncing from current URL. Apt:', aptFromCurrentUrl, 'Hash:', currentHash);
				setActiveApartmentHandle(aptFromCurrentUrl || null);
				if (currentHash) {
					// Attempt to scroll based on current hash if it wasn't handled by state.sectionId
					// scrollToSection(currentHash);
				}
			}
		};
		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, []);

	// useEffect(() => {
	// 	const hash = window.location.hash.replace('#', '');
	// 	if (hash) {
	// 		// TODO: obsługa przewijania do komponentów
	// 	}
	// }, []);

	/**
	 * @description Handles the initial URL 'apt' parameter and hash on page load,
	 * once apartmentsData is available.
	 */
	useEffect(() => {
		if (!displayableApartmentsData || displayableApartmentsData.length === 0 || !projectSetup) return;

		const urlParams = new URLSearchParams(window.location.search);
		const apartmentIdFromUrl = decodeURIComponent(urlParams.get('apt'));
		const hashFromUrl = window.location.hash.substring(1);

		// if (!apartmentIdFromUrl) return;
		let apartmentProcessed = false;

		// const apartmentExists = apartmentsData.some(
		// 	(apt) => apt.id_original === apartmentIdFromUrl
		// );
		// const isSoldAndSoldAreNotAccessible = apartmentsData.some(
		// 	(apt) =>
		// 		apt.id_original === apartmentIdFromUrl &&
		// 		apt.availability === SOLD && //TODO: refactor bim-weblo-panel to use 'sold' instead of 'sell'
		// 		!projectSetup.website.are_sold_apartments_accessible
		// );

		if (apartmentIdFromUrl) {
			if (String(activeApartment) !== String(apartmentIdFromUrl)) {
				const targetApartment = displayableApartmentsData.find(
					(apt) => String(apt.id_original) === String(apartmentIdFromUrl)
				);
				console.log('apartmentIdFromUrl', apartmentIdFromUrl);
				console.log('targetApartment', targetApartment);

				if (targetApartment) {
					const isSoldAndNotAccessible =
						projectSetup.website &&
						targetApartment.availability === SOLD && //TODO: refactor bim-weblo-panel to use 'sold' instead of 'sell' or integer 3 (1 = available, 2 = reserved, 3 = sold)
						!projectSetup.website.are_sold_apartments_accessible;

					const isHidden = targetApartment.hidden === HIDDEN; //TODO: refactor bim-weblo-panel to use .visibility not .hidden

					if (!isSoldAndNotAccessible && !isHidden) {
						setActiveApartmentHandle(apartmentIdFromUrl);
						apartmentProcessed = true;
					} else {
						alert(
							`Przykro nam, lokal "${apartmentIdFromUrl}" nie jest już dostępny.` //TODO: localize this message
						);
						urlParams.delete('apt');
						window.history.replaceState(
							{},
							'',
							`${window.location.pathname}?${urlParams.toString()}${
								window.location.hash
							}`
						);
					}
				} else {
					console.warn(
						`Apartament o ID "${apartmentIdFromUrl}" z parametru URL 'apt' nie został znaleziony, usuwam parametr z URL.`
					);
					alert(
						`Przykro nam, lokal "${apartmentIdFromUrl}" nie został znaleziony.` //TODO: localize this message
					);
					urlParams.delete('apt');
					window.history.replaceState(
						{},
						'',
						`${window.location.pathname}?${urlParams.toString()}${
							window.location.hash
						}`
					);
				}
			} else {
				apartmentProcessed = true; // Already active, consider it handled
			}
		}
		// Handle initial hash for scrolling,
		if (hashFromUrl) {
			// console.log('[Context] Initial URL: Found hash:', hashFromUrl);
			// setTimeout(() => scrollToSection(hashFromUrl), apartmentProcessed ? 100 : 0);
			// scrollToSection(hashFromUrl); // Simpler: scroll immediately
		}

		// if (apartmentExists) {
		// 	if (isSoldAndSoldAreNotAccessible /* lub jest hidden */) {
		// 		console.warn(
		// 			`Lokal o ID "${apartmentIdFromUrl}" jest sprzedany i jego podgląd nie jest dostępny. Usuwam parametr z URL.`
		// 		);
		// 		alert(
		// 			`Przykro nam, lokal "${apartmentIdFromUrl}" nie jest już dostępny.`
		// 		);
		// 		urlParams.delete('apt');
		// 		const newSearchString = urlParams.toString();
		// 		const newUrl = `${window.location.pathname}${
		// 			newSearchString ? `?${newSearchString}` : ''
		// 		}${window.location.hash}`;
		// 		window.history.replaceState({}, '', newUrl);
		// 		return;
		// 	} else if (activeApartment !== apartmentIdFromUrl) {
		// 		setActiveApartmentHandle(apartmentIdFromUrl);
		// 	}
		// } else {
		// 	console.warn(
		// 		`Apartament o ID "${apartmentIdFromUrl}" z parametru URL 'apt' nie został znaleziony, usuwam parametr z URL.`
		// 	);
		// 	alert(
		// 		`Przykro nam, lokal "${apartmentIdFromUrl}" nie został znaleziony.`
		// 	);
		// 	urlParams.delete('apt');
		// 	const newSearchString = urlParams.toString();
		// 	const newUrl = `${window.location.pathname}${
		// 		newSearchString ? `?${newSearchString}` : ''
		// 	}${window.location.hash}`;
		// 	window.history.replaceState({}, '', newUrl);
		// }
	}, [displayableApartmentsData, activeApartment, setActiveApartmentHandle]);

	/**
	 * @description Fetches project.json on initial component mount.
	 */
	// --- DEPRECATED: Old individual data fetching useEffects (now handled in main initialization)
	// useEffect(() => {
	// 	fetchAndSetProjectData();
	// }, []);
	// useEffect(() => {
	// 	fetchAndSetBuildingsData();
	// }, []);
	// useEffect(() => {
	// 	fetchAndSetRawApartmentsData();
	// }, []);

	/**
	 * @description Fetches style.json and compartments.json when projectData is available. //TODO: why projectData is a dependency here?
	 */
	// DEPRECATED: Now fetched in main initialization
	// useEffect(() => {
	// 	// if (!projectData) return;
	// 	if (!buildingsData) return;
	// 	fetchAndSetProjectStyle();
	// 	fetchAndSetCompartmentsDictionary();
	// }, [buildingsData]); //TODO: change to project setup once its ready
	// }, [projectData]);

	/**
	 * @description Parses tiers data from buildings data
	 */
	useEffect(() => {
		if (!buildingsData || buildingsData.length === 0) return

		let newTiers = [];
		buildingsData.forEach((building) => {
			const tiers = building.tiers ?? [];
			tiers.forEach((tier) => {
				const exists = newTiers.some(
					(newTier) =>
						newTier.ref_tier === tier.ref_tier &&
						newTier.building === building.name_original
				);
				if (!exists) {
					const newTier = {
						name: tier.name_original,
						ref_tier: tier.ref_tier,
						id_cad: tier.id_cad,
						building: building.name_original,
						building_id: building.id_cad,
						edifice: building.name_original, //TODO: remove temp. for backward compatibility
						edifice_id: building.id_cad, //TODO: remove temp. for backward compatibility
					};
					newTiers.push(newTier);
				}
			});
		});
		setTiersData((prev) => [...prev, ...newTiers]);
		console.log(
			`%cTiers data parsed`,
			'color: green; font-weight: bold;'
		);
	}, [buildingsData]);




	/**
	 * @description Builds the main allProcessedApartmentsData and displayableApartmentsData lists form rawApartmentsData and CMS/CRM request results.
	 * Handles various states including loading, errors, and CRM integration.
	 */
	useEffect(() => {
		if (!rawApartmentsData || rawApartmentsData.length === 0 || !projectSetup) {
			return;
		}

		if (!isExternalCRM && !isInternalCRM) {
			const processedApartmentsList = buildApartmentsDataList({
				baseData: rawApartmentsData,
				projectSetup,
			});
			const displayableApartmentsList = processedApartmentsList.filter(
				(apt) => apt.visibility !== HIDDEN
			);

			setAllProcessedApartmentsData(processedApartmentsList);
			setDisplayableApartmentsData(displayableApartmentsList);
			// rawFilteredDataRef.current = displayableApartmentsList;
			// updateFilteredApartmentsWithFavoriteFlag();

			if (loading) setLoading(false);
			// return;
		}

		const isCrmFullyReady =
			isExternalCRM &&
			crmDictionary &&
			!dictionaryError &&
			rawCrmData &&
			!crmError;
		const isCmsFullyReady = isInternalCRM && !cmsError && rawCmsData;
		const isWaitingForCrmData =
			isExternalCRM &&
			!dictionaryError &&
			!crmError &&
			(!crmDictionary || !rawCrmData);
		const isWaitingForCmsData = isInternalCRM && !cmsError && !rawCmsData;

		if (isWaitingForCrmData || isWaitingForCmsData) {
			console.log("%cWaiting for CRM/CMS data...", "color: orange; font-weight: bold;");
			//how to handle case when there is a mistake in project_setup internalCRM is set to `true` but there is no actual crm data for any apartment? we would wait forever...
			return;
		}

		// Cases that CRM or CMS is enabled but has errors. Show alert only once. Run buildApartmentsDataList to build list with unknown availability and no prices, block apartment view.
		if (
			(isExternalCRM && (dictionaryError || crmError)) ||
			(isInternalCRM && cmsError)
		) {
			console.error(
				'Context: Wystąpił błąd ze słownikiem, danymi CRM lub CMS.',
				{
					dictionaryError,
					crmError,
					cmsError,
				}
			);
			if (!crmGlobalErrorAlertShown.current) {
				alert(
					`Przepraszamy, wystąpił nieoczekiwany błąd podczas synchronizacji z bazą danych. 
					Aktualne dane apartamentow nie są dostępne, prosimy o kontakt z działem sprzedaży.` //TODO: localize this message
				);
				crmGlobalErrorAlertShown.current = true;
			}
			// setArePricesVisible(false); // Show prices as unknown
			setApartmentsBlockedWithoutData(true); // Block apartment view

			const apartmentsListOnDataError = buildApartmentsDataList({
				baseData: rawApartmentsData,
				cmsData: rawCmsData,
				crmData: rawCrmData,
				isExternalCRM,
				isInternalCRM,
				dictionaryError,
				crmError,
				cmsError,
				projectSetup,
			});
			const displayableApartmentsList = apartmentsListOnDataError.filter(
				(apt) => apt.visibility !== HIDDEN
			);
			setAllProcessedApartmentsData(apartmentsListOnDataError);
			setDisplayableApartmentsData(displayableApartmentsList);
			rawFilteredDataRef.current = displayableApartmentsList;
			updateFilteredApartmentsWithFavoriteFlag();

			if (loading) setLoading(false);

			return;
		}

		// Case that base rawApartmentsData, as well as CRM and or CMS data is available and has no errors:
		if (isCrmFullyReady || isCmsFullyReady) {
			const processedApartmentsList = buildApartmentsDataList({
				baseData: rawApartmentsData,
				cmsData: rawCmsData,
				crmData: rawCrmData,
				isExternalCRM,
				isInternalCRM,
				dictionaryError,
				crmError,
				cmsError,
				projectSetup,
			});
			const displayableApartmentsList = processedApartmentsList.filter(
				(apt) => apt.visibility !== HIDDEN
			);

			setAllProcessedApartmentsData(processedApartmentsList);
			setDisplayableApartmentsData(displayableApartmentsList);
			// rawFilteredDataRef.current = displayableApartmentsList;
			// updateFilteredApartmentsWithFavoriteFlag();

			if (loading) setLoading(false);
			// return;
		}
		console.log(`%cProject data complete`, 'color: blue; font-weight: bold;');
	}, [
		isExternalCRM,
		isInternalCRM,
		rawApartmentsData,
		rawCrmData,
		rawCmsData,
		crmDictionary,
		dictionaryError,
		crmError,
		cmsError,
		loading,
		dataUrl,
		projectSetup,
		// updateFilteredApartmentsWithFavoriteFlag,
	]);

	useEffect(() => {
		if (tiersData.length === 0 || allProcessedApartmentsData.length === 0)
			return;

		const initialConfig = getInitialFiltersConfig({
			// tiers: tiersDataNew,
			tiers: tiersData,
			apartments: allProcessedApartmentsData,
		});

		setInitialFiltersConfig(initialConfig);
		setFiltersConfig(initialConfig);

	}, [allProcessedApartmentsData]);

	useEffect(() => {
		if (areFilterConfigsEqual(filtersConfig, initialFiltersConfig)) {
			setIsFilterActive(false);
		} else {
			setIsFilterActive(true);
		}
	}, [filtersConfig, initialFiltersConfig]);

	useEffect(() => {
		if (!displayableApartmentsData.length || !filtersConfig) return;
		const filteredApartments = filterAndSortApartmentList({
			data: displayableApartmentsData, //allProcessedApartmentsData, ?? //displayableApartmentsData, ??
			filtersConfig,
			sortConfig: sortingConfig,
		});
		
		// console.log('odfiltrowano do:', filteredApartments.length);
		setFilteredApartmentData(filteredApartments);
	}, [displayableApartmentsData, filtersConfig, sortingConfig]);

	useEffect(() => {
		if (!filteredApartmentData.length) {
			setFilteredApartmentDataWithFavorites([]);
			return;
		}
		
		const dataWithFavorites = filteredApartmentData.map(apt => ({
			...apt,
			isFavorite: favoriteApartmentUUIDs.includes(apt.uuid)
		}));
		
		setFilteredApartmentDataWithFavorites(dataWithFavorites);
	}, [filteredApartmentData, favoriteApartmentUUIDs]);

	// //TODO: refactor or remove this to use imported buildApartmentsDataList function, fetch apartments.json directly and remove edificesData dependency
	// //set apartments data
	// useEffect(() => {
	// 	const buildApartmentsDataListOld = async () => {
	// 		if (!edificesData || edificesData.length === 0) {
	// 			// TODO: refactor BimWebloPanel to edit separate client config file as internal CRM data and use here.
	// 			if (!loading) {
	// 				setApartmentsData([]);
	// 				rawFilteredDataRef.current = [];
	// 				// updateFilteredApartmentsWithFavoriteFlag();
	// 			}
	// 			return; // wait for edificesData
	// 		}

	// 		const isCrmFullyReady =
	// 			isExternalCRM &&
	// 			crmDictionary &&
	// 			!dictionaryError &&
	// 			rawCrmData &&
	// 			!crmError;
	// 		const isWaitingForCrmData =
	// 			isExternalCRM &&
	// 			!dictionaryError &&
	// 			!crmError &&
	// 			(!crmDictionary || !rawCrmData);

	// 		const setupPriceVisibility =
	// 			projectSetup.website.are_apartment_prices_visible ?? true;

	// 		if (isWaitingForCrmData) {
	// 			return; // Wait for crmDictionary that may be loading
	// 		}
	// 		if (isExternalCRM && (dictionaryError || crmError)) {
	// 			console.error('Context: Wystąpił błąd z danymi CRM lub słownikiem.', {
	// 				dictionaryError,
	// 				crmError,
	// 			});
	// 			if (!crmGlobalErrorAlertShown.current) {
	// 				alert(
	// 					`Przepraszamy, wystąpił nieoczekiwany błąd podczas synchronizacji z bazą danych. Aktualne dane apartamentow nie są dostępne, prosimy o kontakt z działem sprzedaży.`
	// 				);
	// 				crmGlobalErrorAlertShown.current = true;
	// 			}
	// 			setArePricesVisible(setupPriceVisibility); // Show prices as unknown only if setup allows it
	// 			setApartmentsBlockedWithoutData(true); // Block apartment view

	// 			let apartmentsListOnDataError = edificesData // TODO: refactor BimWebloPanel to edit separate client config file as internal CRM data and use here.
	// 				.flatMap((edifice) => {
	// 					const apartmentsInEdifice = edifice.apartments || [];
	// 					return apartmentsInEdifice.map((sourceApartment) => {
	// 						const apartment = { ...sourceApartment };
	// 						// apartment.availability = UNKNOWN;
	// 						apartment.availability = AVAILABLE; //TODO: decide if UNKNOWN or AVAILABLE as default?
	// 						apartment.price = null;
	// 						apartment.hidden = VISIBLE;
	// 						apartment.ref_tier = apartment?.compartments[0]?.ref_tier ?? 0;
	// 						apartment.edifice_name = edifice.name_original ?? null;
	// 						return apartment;
	// 					});
	// 				});

	// 			apartmentsListOnDataError.sort((a, b) => {
	// 				if (a.id_cad && b.id_cad) {
	// 					return a.id_cad.localeCompare(b.id_cad);
	// 				} else if (a.ordinal && b.ordinal) {
	// 					return Number(a.ordinal) - Number(b.ordinal);
	// 				} else if (a.id && b.id) {
	// 					const idA = String(a.id).toLowerCase();
	// 					const idB = String(b.id).toLowerCase();
	// 					if (idA < idB) return -1;
	// 					if (idA > idB) return 1;
	// 					return 0;
	// 				}
	// 				return 0;
	// 			});

	// 			setApartmentsData(apartmentsListOnDataError);
	// 			rawFilteredDataRef.current = apartmentsListOnDataError;
	// 			// updateFilteredApartmentsWithFavoriteFlag();
	// 			if (loading) setLoading(false);
	// 			return;
	// 		}

	// 		let newApartments = [];
	// 		let calculatedArePricesVisible =
	// 			projectSetup.website.are_apartment_prices_visible || false;

	// 		for (const edifice of edificesData) {
	// 			// TODO: refactor BimWebloPanel to edit separate client config file as internal CRM data and use here.
	// 			const sourceEdificeApartments = edifice.apartments ?? [];

	// 			for (const sourceApartment of sourceEdificeApartments) {
	// 				const apartment = { ...sourceApartment };

	// 				const exists = newApartments.some(
	// 					(newApartment) => newApartment.id_cad === apartment.id_cad //TODO: remember to refactor to 'uuid' or 'id_cad'
	// 				);
	// 				if (exists) {
	// 					continue;
	// 				}

	// 				let crmProduct = null;
	// 				if (isCrmFullyReady) {
	// 					crmProduct = findCrmProductById(
	// 						apartment.id_original ?? apartment.id,
	// 						rawCrmData,
	// 						crmDictionary
	// 					);
	// 				}

	// 				// 1. Checking CRM for apartment visibility -----------------------------------
	// 				let finalHiddenStatus = //TODO: refactor apartment.hidden to apartment.visibility in BimWebloPanel plugin, then here as well.
	// 					apartment.hidden === HIDDEN ? HIDDEN : VISIBLE; //TODO: decide if we should use HIDDEN as default instead of VISIBLE

	// 				if (isExternalCRM) {
	// 					// isCrmFullyReady assumed true, errors handled above
	// 					const crmVisibilityDecision = calculateApartmentCrmVisibility(
	// 						crmProduct,
	// 						crmDictionary.unit_visibility
	// 					);

	// 					if (crmVisibilityDecision === HIDDEN_IN_CRM) {
	// 						finalHiddenStatus = HIDDEN;
	// 					} else if (crmVisibilityDecision === VISIBLE_IN_CRM) {
	// 						finalHiddenStatus = VISIBLE;
	// 					}
	// 				} else {
	// 					// TODO: refactor BimWebloPanel to edit separate client config file as internal CRM data and use here.
	// 				}
	// 				apartment.hidden = finalHiddenStatus; //TODO: refactor apartment.hidden to apartment.visibility in BimWebloPanel plugin, then here as well.

	// 				if (apartment.hidden === HIDDEN) {
	// 					//TODO: refactor apartment.hidden to apartment.visibility in BimWebloPanel plugin, then here as well.
	// 					continue; // Stop processing this apartment if it is hidden, move to the next one
	// 				}

	// 				// 2. Checking CRM for apartment availability ---------------------------------
	// 				let finalAvailabilityStatus = apartment.availability || AVAILABLE; //TODO: AVAILABLE or UNKNOWN as default?

	// 				if (isExternalCRM) {
	// 					// isCrmFullyReady assumed true, errors handled above
	// 					finalAvailabilityStatus = calculateApartmentCrmAvailability(
	// 						crmProduct,
	// 						crmDictionary.availability_data
	// 					);
	// 				} else {
	// 					// TODO: refactor BimWebloPanel to edit separate client config file as internal CRM data and use here.
	// 				}
	// 				apartment.availability = finalAvailabilityStatus;

	// 				// 3. Checking CRM for apartment price ----------------------------------------
	// 				// 3.1 Setting global price visibility flag------------------------------------
	// 				// TODO: Currently, prices are visible or invisible everywhere, consider selectively setting price visibility, based on a flag for each product.

	// 				if (isCrmFullyReady && !calculatedArePricesVisible) {
	// 					// Check while false
	// 					if (
	// 						crmProduct &&
	// 						crmDictionary.price_data &&
	// 						crmDictionary.price_data.prices_visibility
	// 					) {
	// 						const priceVisibilityConfig =
	// 							crmDictionary.price_data.prices_visibility;
	// 						if (
	// 							priceVisibilityConfig.key &&
	// 							typeof priceVisibilityConfig.key === 'string' &&
	// 							priceVisibilityConfig.value_visible !== undefined
	// 						) {
	// 							const crmPriceVisibleValue = String(
	// 								crmProduct[priceVisibilityConfig.key]
	// 							);
	// 							if (
	// 								crmPriceVisibleValue ===
	// 								String(priceVisibilityConfig.value_visible)
	// 							) {
	// 								calculatedArePricesVisible = true;
	// 							}
	// 						}
	// 					}
	// 				}

	// 				// 3.2 Setting apartment price ------------------------------------------------
	// 				let finalPrice = apartment.price || null;

	// 				if (isExternalCRM) {
	// 					// isCrmFullyReady assumed true, errors handled above
	// 					if (
	// 						finalAvailabilityStatus === SOLD ||
	// 						finalAvailabilityStatus === UNKNOWN
	// 					) {
	// 						finalPrice = null;
	// 					} else {
	// 						finalPrice = calculateApartmentCrmPrice(
	// 							crmProduct,
	// 							crmDictionary.price_data
	// 						);
	// 					}
	// 				} else {
	// 					// TODO: refactor BimWebloPanel to edit separate client config file as internal CRM data and use here.
	// 					if (
	// 						finalAvailabilityStatus === SOLD ||
	// 						finalAvailabilityStatus === UNKNOWN
	// 					) {
	// 						finalPrice = null;
	// 					}
	// 				}
	// 				apartment.price = finalPrice;

	// 				// 4. Checking CRM for apartment area -----------------------------------------
	// 				let finalArea = apartment.area_original;

	// 				if (isExternalCRM) {
	// 					const crmArea = calculateApartmentCrmArea(
	// 						crmProduct,
	// 						crmDictionary.unit_area_key
	// 					);
	// 					if (crmArea !== null) {
	// 						const sourceAreaRounded = parseFloat(
	// 							apartment.area_original
	// 						).toFixed(2);
	// 						const crmAreaRounded = parseFloat(crmArea).toFixed(2);

	// 						if (sourceAreaRounded !== crmAreaRounded) {
	// 							// console.log(
	// 							// 	`Apartment ${
	// 							// 		apartment.id_original ?? apartment.id
	// 							// 	}: Area updated from CRM. Original: ${
	// 							// 		apartment.area_original
	// 							// 	}, CRM: ${crmArea}`
	// 							// ); //TODO: remove second sentence in this log in production
	// 							finalArea = crmArea;
	// 						}
	// 					} else {
	// 						console.warn(
	// 							`Context: Apartment ${
	// 								apartment.id_original ?? apartment.id
	// 							}: Brak lub niepoprawna konfiguracja klucza powierzchni w słowniku CRM. Używam powierzchni źródłowej.`
	// 						);
	// 					}
	// 				} else {
	// 					// TODO: refactor BimWebloPanel to edit separate client config file as internal CRM data and use here.
	// 				}
	// 				apartment.area_original = finalArea;

	// 				// 5. Additional CRM apartment properties -------------------------------------

	// 				if (isExternalCRM) {
	// 					apartment.unit_crm_id =
	// 						crmProduct?.[crmDictionary.unit_crm_id_key] ?? null;
	// 					apartment.building_crm_id =
	// 						crmProduct?.[crmDictionary.building_crm_id_key] ?? null;
	// 					apartment.project_crm_id =
	// 						crmProduct?.[crmDictionary.project_crm_id_key] ?? null;
	// 				}

	// 				// 6. Other non-CRM apartment properties --------------------------------------

	// 				apartment.ref_tier = apartment?.compartments[0]?.ref_tier ?? 0;
	// 				apartment.edifice_name = edifice.name_original ?? null;

	// 				newApartments.push(apartment);
	// 			}
	// 		}

	// 		newApartments.sort((a, b) => {
	// 			if (a.id_cad && b.id_cad) {
	// 				return a.id_cad.localeCompare(b.id_cad);
	// 			} else if (a.ordinal && b.ordinal) {
	// 				return Number(a.ordinal) - Number(b.ordinal);
	// 			} else if (a.id && b.id) {
	// 				const idA = String(a.id).toLowerCase();
	// 				const idB = String(b.id).toLowerCase();
	// 				if (idA < idB) return -1;
	// 				if (idA > idB) return 1;
	// 				return 0;
	// 			}
	// 			return 0;
	// 		});

	// 		setArePricesVisible(calculatedArePricesVisible);
	// 		setApartmentsData(newApartments);
	// 		// setFilteredApartmentData(newApartments)
	// 		rawFilteredDataRef.current = newApartments;
	// 		// updateFilteredApartmentsWithFavoriteFlag(); // zastępuje setFilteredApartmentData(newApartments)

	// 		// console.log(
	// 		// 	`%cGet apartments from edifices`,
	// 		// 	'color: green; font-weight: bold;'
	// 		// );
	// 		console.log(
	// 			`%cProject data complete`,
	// 			'color: royalblue; font-weight: bold;'
	// 		);
	// 		setLoading(false);
	// 	};

	// 	buildApartmentsDataListOld();
	// }, [
	// 	edificesData,
	// 	isExternalCRM,
	// 	rawCrmData,
	// 	rawCmsData,
	// 	crmDictionary,
	// 	dictionaryError,
	// 	crmError,
	// 	cmsError,
	// 	loading,
	// 	dataUrl,
	// 	// updateFilteredApartmentsWithFavoriteFlag,
	// ]);

	// useEffect(() => {
	// 	updateFilteredApartmentsWithFavoriteFlag();
	// }, [favoriteApartmentUUIDs]);

	/**
	 * @description Fetches the CRM dictionary if external CRM is enabled.
	 * Falls back to default dictionary or sets an error if fetching fails.
	 */
	useEffect(() => {
		if (!isExternalCRM) {
			setCrmDictionary(null);
			return;
		}

		const dictionaryPath = `${dataUrl}crm-dictionary.json`;
		// console.log(
		// 	`%cContext: Attempting to load CRM dictionary from ${dictionaryPath}...`,
		// 	'color: pink;'
		// ); //TODO: remove this log in production
		setDictionaryError(null);

		const fetchAndSetCrmDictionary = async () => {
			try {
				const dictionaryResponse = await fetch(dictionaryPath);
				if (dictionaryResponse.ok) {
					const crmDictionaryData = await dictionaryResponse.json();
					// console.log(
					// 	`%cCRM dictionary loaded to context.`,
					// 	'color: green; font-weight: bold;'
					// );
					// console.log(
					// 	`%cContext: CRM dictionary loaded successfully from ${dictionaryPath}`,
					// 	'color: pink; font-weight: bold;',
					// 	crmDictionaryData
					// ); //TODO: remove this log in production
					setCrmDictionary(crmDictionaryData);
				}
			} catch (error) {
				console.log(
					`%cContext: CRM dictionary fetch error: ${error.status} ${error.statusText}`
				);
				if (Object.keys(crmConfig.default_dictionary).length !== 0) {
					// console.log(
					// 	`%cContext: Using default CRM dictionary as fallback:`,
					// 	'color: yellow; font-weight: bold;',
					// 	crmConfig.default_dictionary //TODO: remove this log in production
					// );
					setCrmDictionary(crmConfig.default_dictionary);
				} else {
					console.error(
						'%cContext: No default CRM dictionary provided, cannot proceed without a valid dictionary.',
						'color: red; font-weight: bold;'
					);
					setDictionaryError(
						'Nie udało się załadować pliku konfiguracyjnego CRM (słownika) i nie podano domyślnego słownika.'
					);
					setCrmDictionary(null);
				}
			}
		};

		if (isExternalCRM) fetchAndSetCrmDictionary();
	}, []);

	/**
	 * @description Fetches and processes CRM data if external CRM is enabled
	 * and crmConfig is available. Sets crmError on failure.
	 */
	useEffect(() => {
		const envVars = {
			REACT_APP_BACKEND_API_RESULT_FILE:
				process.env.REACT_APP_BACKEND_API_RESULT_FILE,
			REACT_APP_BACKEND_API_ENDPOINT:
				process.env.REACT_APP_BACKEND_API_ENDPOINT,
			REACT_APP_BACKEND_API_RESULT_DIR:
				process.env.REACT_APP_BACKEND_API_RESULT_DIR,
		};

		const loadCrmData = async () => {
			if (crmConfig && crmConfig.enabled) {
				//this is the same as isExternalCRM
				setCrmError(null); // Resetuj błąd przed próbą
				// console.log('%cContext: Initiating CRM data fetch...', 'color: green;');
				const data = await fetchAndProcessCrmData(pageUrl, crmConfig, envVars);
				if (data) {
					// 					console.log(
					// 						'%cCRM data received, processed and set to context.',
					// 						'color: green;',
					// 						data //TODO: remove this log in production
					// 					);
					setRawCrmData(data);
				} else {
					console.error(
						'%cContext: Failed to fetch or process CRM data after all attempts.',
						'color: red;'
					);
					setCrmError(
						'Nie udało się załadować danych o cenach i dostępności z systemu CRM.'
					);
					setRawCrmData(null);
				}
			} else {
				console.log(
					'%cContext: CRM integration is disabled.',
					'color: lightblue'
				);
			}
		};

		if (isExternalCRM) loadCrmData();
	}, []);

	/**
	 * @description Fetches and processes CMS data if internal CRM is enabled
	 * and crmConfig is available. Sets cmsError on failure.
	 */
	useEffect(() => {
		const loadCmsData = async () => {
			setCmsError(null);
			const data = await fetchLocalJSON(
				`${dataUrl}internal_crm_result.json`,
				'Error fetching CMS data'
			);
			const isValidCmsData = data && Array.isArray(data.apartments) && data.apartments.length > 0;
			if (isValidCmsData) {
				setRawCmsData(data);
			} else {
				setCmsError(
					'Nie udało się załadować poprawnych danych o cenach i dostępności z CMS.'
				);
				setRawCmsData(null);
			}
		};
		if (isInternalCRM) loadCmsData();
	}, []);

	// const compartmentIdsUsedInProject = apartmentsData.reduce((acc, apartment) => {
	// 	if (apartment.compartments && apartment.compartments.length > 0) {
	// 		apartment.compartments.forEach((compartment) => {
	// 			if (compartment.name_cad && !acc.includes(compartment.name_cad)) {
	// 				acc.push(compartment.name_cad);
	// 			}
	// 		});
	// 	}
	// 	return acc;
	// }, []);
	// console.log(compartmentIdsUsedInProject);

	// 	console.log("projectEdifices", projectEdifices);
	// 	/*from project.json
	// [{name: 'A', visualization: 'main.jpg', layer: 'main.svg'},
	// {name: 'B', visualization: 'main.jpg', layer: 'main.svg'},
	// {name: 'C', visualization: 'main.jpg', layer: 'main.svg'},
	// {name: 'D', visualization: 'main.jpg', layer: 'main.svg'}]
	// 	*/
	// 	console.log("edificesData", edificesData); // array of all edifices with all data - apartments and lot
	// 	console.log("tiersData", tiersData); // array of tiers from all edifices
	// 	/*
	// [{name: '0', ref_tier: 0, edifice: 'A'},
	// {name: '1', ref_tier: 1, edifice: 'A'},
	// {name: '2', ref_tier: 2, edifice: 'A'},
	// {name: '3', ref_tier: 3, edifice: 'A'},
	// {name: '4', ref_tier: 4, edifice: 'A'},
	// {name: '0', ref_tier: 0, edifice: 'B'},
	// {name: '1', ref_tier: 1, edifice: 'B'},
	// {name: '2', ref_tier: 2, edifice: 'B'},
	// {name: '3', ref_tier: 3, edifice: 'B'},
	// {name: '4', ref_tier: 4, edifice: 'B'}]
	// 	*/
	// 	console.log("currentIndex", currentIndex); //active animation frame index
	// 	console.log("layoutList", layoutList); //bool? apartmentList as list of grid?
	// console.log("edificesData", edificesData);
	return (
		<Context.Provider
			value={{
				activeLang,
				setActiveLang,
				loading,
				setLoading,
				projectData,
				// projectEdifices,
				// edificesData,
				activeBuilding,
				setActiveBuilding,
				tiersData,
				// tiersDataNew,
				activeTier,
				setActiveTier,
				// apartmentsData,
				// GŁÓWNE LISTY DANYCH:
				allProcessedApartmentsData,        // Wszystkie przetworzone (z CRM/CMS)
				displayableApartmentsData,         // Widoczne (bez HIDDEN)
				apartmentsToDisplayData: filteredApartmentDataWithFavorites, // Finalna lista do UI
				activeApartment,
				setActiveApartment,
				setActiveApartmentHandle,
				activeApartmentSelectedTier, setActiveApartmentSelectedTier,
				isDesktop,
				isMobile,
				mobileActiveView,
				setMobileActiveView,
				projectStyle,
				showView,
				setShowView,
				visualizationsViewRef,
				tiersViewRef,
				activeLayer,
				setActiveLayer,
				currentIndex,
				setCurrentIndex,
				dataUrl,
				filteredApartmentData: filteredApartmentDataWithFavorites, // apartmentsData with isFavorite flag
				setFilteredApartmentData: handleSetFilteredApartmentData, // custom setter to update filtered data with isFavorite flag
				isFilterActive,
				setIsFilterActive,
				overviewLayerRef,
				scrollPosition,
				setScrollPosition,
				apartmentViewRef,
				favoritesViewRef,
				layoutList,
				setLayoutList,
				updateUrlHash,
				showRotateInstruction,
				setShowRotateInstruction,
				favoriteApartmentUUIDs,
				toggleFavoriteApartmentHandler,
				removeAllFavoritesHandler,
				arePricesVisible,
				compartmentsDictionary,
				setCompartmentsDictionary,
				projectSetup,
				currentModelScope,
				setCurrentModelScope,
				activeApartmentData,
				setActiveApartmentData,
				navigateToApartmentInDirectionHandler,
				applyFiltersHandler,
				resetAllFiltersHandler,
				restorePreviousFiltersHandler,
				previousFiltersConfig,
				previousFiltersUIState,
				setPreviousFiltersConfig,
				setPreviousFiltersUIState,
				initialFiltersConfig,
				filtersConfig,
				applySortingHandler,
				sortingConfig,
				buildingsData,
				filteredApartmentsCount: filteredApartmentData.length, //filteredApartmentDataWithFavorites
				allApartmentsCount: allProcessedApartmentsData.length,
				displayableApartmentsCount: displayableApartmentsData.length,
				
			}}
		>
			{children}
		</Context.Provider>
	);
};

export default ContextProvider;
